import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { auth } from './firebase'; 
import { signOut } from 'firebase/auth'; 
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Gallery from './pages/Gallery';
import Video from './pages/Video';
import './i18n';
import { useTranslation } from 'react-i18next';
import './index.css';
import brandLogo from './assets/images/LOGO.png';
import Atmosphere from './components/Atmosphere';

const destinations = [
  ['/', 'home'], ['/idea', 'about'], ['/gallery', 'gallery'], ['/video', 'Video'],
  ['/contact', 'contact'],
];

function Project() {
  const { i18n, t } = useTranslation();
  const { pathname, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 24);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const lang = i18n.resolvedLanguage || 'en';
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, isAuthorized, locationKey]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = event => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        document.getElementById('menu-trigger')?.focus();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [menuOpen]);

  const handleLogout = async () => {
    try {
      await signOut(auth); 
      setIsAuthorized(false); 
    } catch (error) {
      console.error("Errore durante il logout:", error);
    }
  };

  return (
    <div lang={lang} dir={i18n.dir(lang)} className={'project-shell' + (isAuthorized && pathname !== '/' && pathname !== '/donate' ? ' project-shell--content' : '')}>
      {isAuthorized && <nav className={'project-nav' + (scrolled ? ' scrolled' : '')} aria-label={t('menu_label')}>
        <Link className="project-brand" to="/" onClick={() => setMenuOpen(false)}>
          <img src={brandLogo} alt="Soul Rock Cafe" />
          <span className="project-brand-wordmark">SOUL ROCK CAFE<small>RAS AL KHAIMAH</small></span>
        </Link>
        <button id="menu-trigger" className="project-menu-trigger" aria-expanded={menuOpen}
          aria-controls="project-links" onClick={() => setMenuOpen(!menuOpen)}>
          <span aria-hidden="true">☰</span> {t('menu_label')}
        </button>
        <div id="project-links" className={'project-links' + (menuOpen ? ' open' : '')}>
          {destinations.map(([path, label]) => (
            <NavLink key={path} end to={path} onClick={() => setMenuOpen(false)}>{t(label)}</NavLink>
          ))}
          <div className="project-languages">
            {['en', 'it', 'ar'].map(code => (
              <button key={code} aria-pressed={lang === code} onClick={() => {
                i18n.changeLanguage(code);
                setMenuOpen(false);
              }}>{code.toUpperCase()}</button>
            ))}
          </div>
          {isAuthorized && <button className="project-signout" onClick={() => { setMenuOpen(false); handleLogout(); }}>{t('sign_out')}</button>}
        </div>
      </nav>}
      {isAuthorized ? (
          <Atmosphere theme={pathname === '/' || pathname === '/donate' ? 'marble' : pathname === '/idea' ? 'story' : pathname === '/gallery' ? 'denim' : pathname === '/video' ? 'vinyl' : 'plain'}>
          <Routes>
            <Route path="/" element={<Home key={locationKey} />} />
            <Route path="/idea" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/video" element={<Video />} />
            <Route path="/pitch-deck" element={<Navigate to="/" replace />} />
            <Route path="/donate" element={<Home />} />
            <Route path="/ethics" element={<Navigate to="/" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Atmosphere>
      ) : (
        <Atmosphere theme="access">
        <Login onLoginSuccess={() => {
          navigate('/', { replace: true });
          setMenuOpen(false);
          setIsAuthorized(true);
        }} />
        </Atmosphere>
      )}
      {isAuthorized && ['/idea', '/contact'].includes(pathname) && <footer className="footer-proof">
        <p className="footer-brand">SOUL ROCK CAFE</p>
        <small>{t('footer_protection')}</small>
        <a className="footer-email" href="mailto:info@soulrockcafe.com">info@soulrockcafe.com</a>
      </footer>}
    </div>
  );
}

export default function App() {
  return <Router><Project /></Router>;
}
